import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_TINYCMS_FEED_KEYS,
  GET_TINYCMS_SITE,
  CREATE_TINYCMS_FEED_KEY,
  REVOKE_TINYCMS_FEED_KEY,
  UPDATE_TINYCMS_FEED_KEY,
} from '../../graphql';
import { useCopy } from '../../lib/copyToClipboard';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';
import { SETTINGS_HISTORY_QUERY } from './SettingsHistory';
import { CONTENT_API_DOCS } from '../../config/helpLinks';

/**
 * Site settings → Licensed feeds. The site's free feed (/feed.xml) has titles
 * and summaries; a key lets a licensee (a publisher, an app, a partner) pull
 * the published articles in full from /feeds/licensed.xml or .json, and read
 * published articles, pages, media and authors as JSON through the Content API
 * at /feeds/v1/. The owner issues one key per licensee, picks what it reads, sees
 * what each takes, and can turn any off. An existing key stays articles-only
 * until its owner widens it with Change access (same key, nothing to resend).
 *
 * A key is shown once, when it is made (the server keeps only a hash). Keys
 * are managed here by account admins and never over MCP; members see the list
 * read-only (the server enforces it).
 *
 * Pro only. On another plan the free feed stays, new keys are offered as an
 * upgrade, and existing keys are kept but paused until the site is back on Pro.
 */

type ContentType = 'articles' | 'pages' | 'media' | 'authors';

const CONTENT_TYPES: Array<{ key: ContentType; name: string }> = [
  { key: 'articles', name: 'Articles' },
  { key: 'pages', name: 'Pages' },
  { key: 'media', name: 'Media' },
  { key: 'authors', name: 'Authors' },
];

interface FeedKey {
  id: string;
  label: string;
  keyPrefix: string;
  contentTypes?: ContentType[];
  collections: string[];
  tags: string[];
  expiresAt: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  status: 'active' | 'expired' | 'revoked';
  usage30d?: { requests: number; items: number };
}

interface Collection {
  slug: string;
  name: string;
}

const STATUS: Record<FeedKey['status'], { label: string; bg: string; color: string }> = {
  active: { label: 'Active', bg: 'var(--ink-green-bg)', color: 'var(--ink-green-ink)' },
  expired: { label: 'Expired', bg: 'var(--ink-amber-bg)', color: 'var(--ink-amber)' },
  revoked: { label: 'Turned off', bg: 'var(--ink-bg3)', color: 'var(--ink-ink3)' },
};

const PLAN_LINK = '/settings/billing';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const ago = (iso: string) => {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

const count = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Tomorrow, as a date input's min. */
const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** What a key reads, as its row says it: "All articles", "Articles in MLB, media, authors". */
function scopeText(k: FeedKey, names: Map<string, string>) {
  const types = k.contentTypes?.length ? k.contentTypes : ['articles'];
  const parts: string[] = [];
  if (types.includes('articles')) {
    const within: string[] = [];
    if (k.collections.length) within.push(k.collections.map((s) => names.get(s) || s).join(', '));
    if (k.tags.length) within.push(`tagged ${k.tags.join(', ')}`);
    // Both set: an article must match a collection AND a tag (the server's rule).
    parts.push(within.length ? `Articles in ${within.join(', ')}` : 'All articles');
  }
  for (const t of types) if (t !== 'articles') parts.push(t);
  const text = parts.join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const tagList = (tags: string) =>
  tags
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

/** What a key reads, as the form holds it. */
interface Access {
  types: ContentType[];
  limit: boolean;
  picked: string[];
  tags: string;
}

const accessOf = (k?: FeedKey | null): Access => ({
  types: k?.contentTypes?.length ? k.contentTypes : ['articles'],
  limit: Boolean(k && (k.collections.length || k.tags.length)),
  picked: k?.collections || [],
  tags: (k?.tags || []).join(', '),
});

/** Why this access can't be saved, or null. */
const accessProblem = (a: Access) => {
  if (!a.types.length) return 'Pick at least one thing they get.';
  if (a.types.includes('articles') && a.limit && !a.picked.length && !tagList(a.tags).length) {
    return 'Pick at least one section or tag, or choose All sections.';
  }
  return null;
};

/** The server's input for this access. Limits apply to articles; without articles there are none. */
const accessInput = (a: Access) => {
  const limited = a.types.includes('articles') && a.limit;
  return {
    contentTypes: a.types,
    collections: limited ? a.picked : [],
    tags: limited ? tagList(a.tags) : [],
  };
};

/** "What they get", shared by a new key and Change access. */
function AccessFields({
  access,
  collections,
  onChange,
}: {
  access: Access;
  collections: Collection[];
  onChange: (next: Access) => void;
}) {
  const { types, limit, picked, tags } = access;
  const set = (patch: Partial<Access>) => onChange({ ...access, ...patch });
  return (
    <div className="ink-site-label">
      What they get
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {CONTENT_TYPES.map((t) => {
          const on = types.includes(t.key);
          return (
            <button
              key={t.key}
              type="button"
              className="ink-chip"
              data-active={on}
              aria-pressed={on}
              onClick={() =>
                set({ types: on ? types.filter((x) => x !== t.key) : [...types, t.key] })
              }
            >
              {t.name}
            </button>
          );
        })}
      </div>
      {types.includes('articles') ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className="ink-site-row-desc" style={{ margin: 0, fontWeight: 400 }}>
            Articles from
          </span>
          <span className="ink-seg">
            <button type="button" data-active={!limit} onClick={() => set({ limit: false })}>
              All sections
            </button>
            <button type="button" data-active={limit} onClick={() => set({ limit: true })}>
              Only some
            </button>
          </span>
        </div>
      ) : null}
      {types.includes('articles') && limit ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {collections.length ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {collections.map((c) => {
                const on = picked.includes(c.slug);
                return (
                  <button
                    key={c.slug}
                    type="button"
                    className="ink-chip"
                    data-active={on}
                    aria-pressed={on}
                    onClick={() =>
                      set({ picked: on ? picked.filter((s) => s !== c.slug) : [...picked, c.slug] })
                    }
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          ) : null}
          <input
            className="ink-site-input"
            value={tags}
            aria-label="Tags, separated by commas"
            placeholder="Tags, separated by commas"
            onChange={(e) => set({ tags: e.target.value })}
          />
        </div>
      ) : null}
      <span className="ink-site-row-desc" style={{ margin: 0, fontWeight: 400 }}>
        Published versions only.
        {types.includes('articles') ? ' Articles bring their public custom fields.' : ''}
        {types.includes('media')
          ? ' Media comes with its descriptions; leave it off if you license photos from others.'
          : ''}
      </span>
    </div>
  );
}

/** Change access, in place above the keys: the same key, so there is nothing new to send. */
function ChangeAccess({
  accountId,
  k,
  collections,
  onClose,
}: {
  accountId?: string | null;
  k: FeedKey;
  collections: Collection[];
  onClose: () => void;
}) {
  const [label, setLabel] = useState(k.label);
  const [access, setAccess] = useState<Access>(() => accessOf(k));
  const [error, setError] = useState('');
  const [save, { loading }] = useMutation(UPDATE_TINYCMS_FEED_KEY, {
    refetchQueries: [SETTINGS_HISTORY_QUERY],
    onError: (err) => setError(err.message),
    onCompleted: onClose,
  });
  const problem = accessProblem(access);
  return (
    <form
      className="ink-site-card"
      style={{ margin: '12px 0', padding: '14px 16px' }}
      onSubmit={(e) => {
        e.preventDefault();
        setError('');
        if (!label.trim()) return setError('Say who the key is for.');
        if (problem) return setError(problem);
        save({
          variables: {
            accountId,
            id: k.id,
            input: { label: label.trim(), ...accessInput(access) },
          },
        });
      }}
    >
      <div className="ink-site-row-title">Change what {k.label} can read</div>
      <div style={{ display: 'grid', gap: 16, marginTop: 12 }}>
        <label className="ink-site-label">
          Who it’s for
          <input
            className="ink-site-input"
            value={label}
            maxLength={120}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <AccessFields access={access} collections={collections} onChange={setAccess} />
        <span className="ink-site-row-desc" style={{ margin: 0 }}>
          Same key, so there’s nothing new to send them.
        </span>
      </div>
      {error ? (
        <p className="ink-site-hint" style={{ color: 'var(--ink-red)' }}>
          {error}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" className="ink-btn" onClick={onClose} disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="ink-btn ink-btn-primary" disabled={loading}>
          {loading ? 'Saving…' : 'Save access'}
        </button>
      </div>
    </form>
  );
}

export function FeedKeysSettings() {
  const { accountId } = useAccount();
  const { copy, state: copyState } = useCopy();
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<FeedKey | null>(null);
  const [changing, setChanging] = useState<FeedKey | null>(null);
  const [showOff, setShowOff] = useState(false);

  const { data, loading, refetch } = useQuery(GET_TINYCMS_FEED_KEYS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: siteData, loading: siteLoading } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  // #feeds and #api-keys (AI Agents' write key link) open there. This list loads
  // above #api-keys and moves it (the feed rows need the site's address), so
  // scroll again once its height is known.
  const settling = loading || siteLoading;
  useEffect(() => {
    if (settling) return;
    const id = window.location.hash.replace('#', '');
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [settling]);
  const info = data?.tinycmsFeedKeys;
  const keys: FeedKey[] = useMemo(() => info?.keys || [], [info]);
  const canManage = Boolean(info?.canManage);
  // Older servers don't send it: treat as allowed and let the server refuse.
  const allowed = info?.allowed !== false;
  const siteUrl: string | null = siteData?.tinycmsSite?.siteUrl || null;
  const collections: Collection[] = useMemo(
    () => siteData?.tinycmsSite?.working?.collections || [],
    [siteData]
  );
  const names = useMemo(() => new Map(collections.map((c) => [c.slug, c.name])), [collections]);

  const [revoke, { loading: revokingNow }] = useMutation(REVOKE_TINYCMS_FEED_KEY, {
    refetchQueries: [SETTINGS_HISTORY_QUERY],
    onError: (err) => setError(err.message),
    onCompleted: () => {
      setRevoking(null);
      refetch();
    },
  });

  if (loading && !info) return <Loader label="Loading" className="p-6 text-cmp-ink-2" />;

  const live = keys.filter((k) => k.status === 'active');
  const off = keys.filter((k) => k.status !== 'active');
  const shown = showOff ? keys : live;

  const copyLabel = (key: string, idle = 'Copy') =>
    copyState(key) === 'copied' ? 'Copied' : copyState(key) === 'failed' ? 'Couldn’t copy' : idle;

  const addresses: Array<{ key: string; name: string; desc: string; path: string; paid: boolean }> =
    [
      {
        key: 'xml',
        name: 'Full articles · RSS',
        desc: 'For news readers, publishers and most website tools',
        path: '/feeds/licensed.xml',
        paid: true,
      },
      {
        key: 'json',
        name: 'Full articles · JSON',
        desc: 'The same feed in JSON Feed format, for feed tools that prefer it',
        path: '/feeds/licensed.json',
        paid: true,
      },
      {
        key: 'api',
        name: 'Content API',
        desc: 'For developers: published articles, pages, media and authors as JSON. Add /articles, /pages, /media or /authors.',
        path: '/feeds/v1/',
        paid: true,
      },
      {
        key: 'free',
        name: 'Free feed',
        desc: 'Titles and summaries, for anyone.',
        path: '/feed.xml',
        paid: false,
      },
    ].filter((a) => allowed || !a.paid);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="ink-site-card">
        <div className="ink-site-row">
          <span>
            <span className="ink-site-row-title">Feed addresses</span>
            <span className="ink-site-row-desc" style={{ display: 'block' }}>
              {allowed
                ? 'Send these to each licensee, with their key.'
                : 'Anyone can follow your site with this address.'}
            </span>
          </span>
          <span />
        </div>
        {siteUrl ? (
          <>
            {addresses.map((a) => (
              <div key={a.key} className="ink-site-row">
                <div style={{ minWidth: 0 }}>
                  {/* Which addresses need a key, at a glance (Prototype). */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className="ink-site-row-title">{a.name}</span>
                    <span
                      className="ink-site-pill"
                      style={
                        a.paid
                          ? { background: 'var(--ink-bg3)', color: 'var(--ink-ink2)' }
                          : { background: 'var(--ink-green-bg)', color: 'var(--ink-green-ink)' }
                      }
                    >
                      {a.paid ? 'Needs a key' : 'No key needed'}
                    </span>
                  </div>
                  {/* What it's for, then its address (App Prototype; backlog 69493019). */}
                  <div className="ink-site-row-desc">{a.desc}</div>
                  <div
                    className="ink-site-row-desc"
                    style={{
                      fontFamily: 'var(--ink-mono)',
                      overflowWrap: 'anywhere',
                      color: 'var(--ink-ink2)',
                    }}
                  >
                    {siteUrl}
                    {a.path}
                  </div>
                </div>
                <button
                  type="button"
                  className="ink-site-link"
                  onClick={(e) => copy(a.key, `${siteUrl}${a.path}`, e)}
                >
                  {copyLabel(a.key)}
                </button>
              </div>
            ))}
            {allowed ? (
              <p className="ink-site-hint">
                Endpoints, an example request and how keys work:{' '}
                <a
                  className="ink-site-link"
                  href={CONTENT_API_DOCS}
                  target="_blank"
                  rel="noreferrer"
                >
                  Content API docs ↗
                </a>{' '}
                Send the link to licensees; the page is open to them without an account.
              </p>
            ) : null}
          </>
        ) : (
          <p className="ink-site-hint">
            Your site needs a web address first. Set one under Domain above.
          </p>
        )}
      </div>

      <div className="ink-site-card">
        <div className="ink-site-row">
          <span>
            {/* "Keys 2 active" with New key on the same row (App Prototype; backlog 12cbc64f). */}
            <span className="ink-site-row-title">
              Keys
              {allowed && live.length ? (
                <span className="ink-conn-head-muted"> {live.length} active</span>
              ) : null}
            </span>
            <span className="ink-site-row-desc" style={{ display: 'block' }}>
              {!allowed
                ? 'Licensing your content comes with the Pro plan. Your free feed works on every plan.'
                : live.length
                  ? 'Each one shows what it took in the last 30 days.'
                  : off.length
                    ? 'No keys are working right now. Make one for each person or company you license to.'
                    : 'No one has a key yet. Make one for each person or company you license to.'}
            </span>
          </span>
          {!allowed ? (
            canManage ? (
              <Link to={PLAN_LINK} className="ink-btn">
                Change plan
              </Link>
            ) : null
          ) : canManage ? (
            <button
              type="button"
              className="ink-btn"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => {
                setError('');
                setCreating(true);
              }}
            >
              New key
            </button>
          ) : null}
        </div>
        {!allowed && live.length ? (
          <p className="ink-bill-note" data-tone="warn" style={{ margin: '14px 0' }}>
            Your keys are kept, but they don’t work on this plan: anyone using one is turned away.
            They start working again when you move back to Pro.
          </p>
        ) : null}
        {changing ? (
          <ChangeAccess
            key={changing.id}
            accountId={accountId}
            k={changing}
            collections={collections}
            onClose={() => {
              setChanging(null);
              refetch();
            }}
          />
        ) : null}
        {shown.map((k) => {
          const s =
            !allowed && k.status === 'active'
              ? { label: 'Paused', bg: 'var(--ink-amber-bg)', color: 'var(--ink-amber)' }
              : STATUS[k.status];
          const usage = k.usage30d || { requests: 0, items: 0 };
          return (
            <div
              key={k.id}
              className="ink-site-row"
              style={k.status === 'active' ? undefined : { opacity: 0.7 }}
            >
              <div style={{ minWidth: 0 }}>
                <span className="ink-site-row-title">{k.label}</span>{' '}
                <span className="ink-site-row-desc" style={{ fontFamily: 'var(--ink-mono)' }}>
                  {k.keyPrefix}…
                </span>
                <div className="ink-site-row-desc">
                  {scopeText(k, names)}
                  {' · '}
                  {k.lastUsedAt ? `Last used ${ago(k.lastUsedAt)}` : 'Not used yet'}
                  {' · '}
                  {count(usage.requests, 'request')}, {count(usage.items, 'article')} in 30 days
                  {' · '}
                  {k.revokedAt
                    ? `Turned off ${day(k.revokedAt)}`
                    : k.expiresAt
                      ? `${k.status === 'expired' ? 'Expired' : 'Expires'} ${day(k.expiresAt)}`
                      : 'Never expires'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="ink-site-pill" style={{ background: s.bg, color: s.color }}>
                  {s.label}
                </span>
                {canManage && allowed && k.status === 'active' ? (
                  <button
                    type="button"
                    className="ink-site-link"
                    onClick={() => {
                      setError('');
                      setChanging(k);
                    }}
                  >
                    Change access
                  </button>
                ) : null}
                {canManage && k.status === 'active' ? (
                  <button
                    type="button"
                    className="ink-site-link"
                    style={{ color: 'var(--ink-ink2)' }}
                    onClick={() => {
                      setError('');
                      setRevoking(k);
                    }}
                  >
                    Turn off
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
        {off.length ? (
          <div className="ink-site-hint">
            <button type="button" className="ink-site-link" onClick={() => setShowOff(!showOff)}>
              {showOff ? 'Hide' : 'Show'} {off.length} turned off
            </button>
          </div>
        ) : null}
        {error && !creating && !revoking ? (
          <p className="ink-site-hint" style={{ color: 'var(--ink-red)' }}>
            {error}
          </p>
        ) : null}
        {!canManage ? (
          <p className="ink-site-hint">Only an account admin can make or turn off keys.</p>
        ) : null}
      </div>

      {creating ? (
        <NewKeyModal
          accountId={accountId}
          collections={collections}
          siteUrl={siteUrl}
          onClose={() => {
            setCreating(false);
            refetch();
          }}
        />
      ) : null}

      {revoking ? (
        <div
          className="ink-modal-back"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !revokingNow) setRevoking(null);
          }}
        >
          <div
            className="ink-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="revoke-key-title"
          >
            <div className="ink-modal-title" id="revoke-key-title">
              Turn off “{revoking.label}”?
            </div>
            <div className="ink-modal-copy">
              The key <span style={{ fontFamily: 'var(--ink-mono)' }}>{revoking.keyPrefix}…</span>{' '}
              stops working right away, and this can’t be undone. To let them back in, make a new
              key.
            </div>
            {error ? (
              <div className="ink-modal-copy" style={{ color: 'var(--ink-red)' }}>
                {error}
              </div>
            ) : null}
            <div className="ink-modal-actions">
              <button type="button" className="ink-btn" autoFocus onClick={() => setRevoking(null)}>
                Keep it
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                style={{ background: 'var(--ink-red)', borderColor: 'var(--ink-red)' }}
                disabled={revokingNow}
                onClick={() => revoke({ variables: { accountId, id: revoking.id } })}
              >
                {revokingNow ? 'Turning off…' : 'Turn off key'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Make a key: who it's for, what it can read, when it stops. Then show it once. */
function NewKeyModal({
  accountId,
  collections,
  siteUrl,
  onClose,
}: {
  accountId?: string | null;
  collections: Collection[];
  siteUrl: string | null;
  onClose: () => void;
}) {
  const { copy, state: copyState } = useCopy();
  const [label, setLabel] = useState('');
  const [access, setAccess] = useState<Access>(() => accessOf());
  const [expires, setExpires] = useState('');
  const [error, setError] = useState('');
  const [made, setMade] = useState<(FeedKey & { key: string }) | null>(null);

  const [create, { loading }] = useMutation(CREATE_TINYCMS_FEED_KEY, {
    refetchQueries: [SETTINGS_HISTORY_QUERY],
    onError: (err) => setError(err.message),
    onCompleted: (res) => setMade(res.createTinycmsFeedKey),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!label.trim()) return setError('Say who the key is for.');
    const problem = accessProblem(access);
    if (problem) return setError(problem);
    create({
      variables: {
        accountId,
        input: {
          label: label.trim(),
          ...accessInput(access),
          // The end of the chosen day, where the owner is.
          ...(expires ? { expiresAt: new Date(`${expires}T23:59:59`).toISOString() } : {}),
        },
      },
    });
  };

  const copyLabel = (key: string, idle: string) =>
    copyState(key) === 'copied'
      ? 'Copied'
      : copyState(key) === 'failed'
        ? 'Couldn’t copy — select it instead'
        : idle;

  return (
    <div
      className="ink-modal-back"
      onMouseDown={(e) => {
        // Never on a click outside once the key is showing: it can't be shown again.
        if (e.target === e.currentTarget && !made && !loading) onClose();
      }}
    >
      <div
        className="ink-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-key-title"
        style={{ width: 'min(500px, 100%)' }}
      >
        {made ? (
          <>
            <div className="ink-modal-title" id="new-key-title">
              Key for {made.label}
            </div>
            <div className="ink-modal-copy">
              Copy it now and keep it somewhere safe. <strong>We won’t show it again</strong> — if
              it’s lost, turn it off and make a new one.
            </div>
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 8,
                background: 'var(--ink-bg2)',
                border: '1px solid var(--ink-line3)',
                font: '500 13.5px/1.5 var(--ink-mono)',
                color: 'var(--ink-ink)',
                overflowWrap: 'anywhere',
                userSelect: 'all',
              }}
              data-feed-key
            >
              {made.key}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" className="ink-btn" onClick={(e) => copy('key', made.key, e)}>
                {copyLabel('key', 'Copy key')}
              </button>
              {siteUrl ? (
                <button
                  type="button"
                  className="ink-btn"
                  onClick={(e) =>
                    copy(
                      'note',
                      [
                        `Feed (RSS): ${siteUrl}/feeds/licensed.xml`,
                        `Feed (JSON): ${siteUrl}/feeds/licensed.json`,
                        `Content API: ${siteUrl}/feeds/v1/ (how it works: ${CONTENT_API_DOCS})`,
                        `Send this header with each request: Authorization: Bearer ${made.key}`,
                      ].join('\n'),
                      e
                    )
                  }
                >
                  {copyLabel('note', 'Copy key with addresses')}
                </button>
              ) : null}
            </div>
            <div className="ink-modal-actions">
              <button type="button" className="ink-btn ink-btn-primary" autoFocus onClick={onClose}>
                I’ve saved it
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="ink-modal-title" id="new-key-title">
              New key
            </div>
            <div className="ink-modal-copy">
              One key for each person or company, so you can see what each takes and turn one off
              without affecting the rest.
            </div>
            <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
              <label className="ink-site-label">
                Who it’s for
                <input
                  className="ink-site-input"
                  value={label}
                  autoFocus
                  maxLength={120}
                  placeholder="e.g. Daily Sports Wire"
                  onChange={(e) => setLabel(e.target.value)}
                />
              </label>
              <AccessFields access={access} collections={collections} onChange={setAccess} />
              <label className="ink-site-label">
                Stops working on
                <input
                  className="ink-site-input"
                  type="date"
                  value={expires}
                  min={tomorrow()}
                  onChange={(e) => setExpires(e.target.value)}
                />
                <span className="ink-site-row-desc" style={{ margin: 0, fontWeight: 400 }}>
                  Optional. Leave empty and it works until you turn it off.
                </span>
              </label>
            </div>
            {error ? (
              <div className="ink-modal-copy" style={{ color: 'var(--ink-red)' }}>
                {error}
              </div>
            ) : null}
            <div className="ink-modal-actions">
              <button type="button" className="ink-btn" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="ink-btn ink-btn-primary" disabled={loading}>
                {loading ? 'Making key…' : 'Make key'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
